/*
  Cart drawer "frequently bought together". For each product in the cart it fetches the
  complementary products set in Search & Discovery (intent=complementary). When there are too
  few, it tops up with related products (intent=related). Results are cached per product so the
  list does not reload when the drawer is re-rendered after a quantity change.
*/
(() => {
  if (customElements.get('movitamins-cart-recs')) return;

  const MAX_SOURCE_PRODUCTS = 4;
  const MAX_ITEMS = 10;
  const MIN_ITEMS = 4;
  const cache = new Map();
  let lastRender = null;
  let lastScroll = 0;

  const fetchRecs = (url, productId, intent) => {
    const key = `${intent}:${productId}`;
    if (!cache.has(key)) {
      const request = fetch(
        `${url}?product_id=${productId}&limit=${MAX_ITEMS}&intent=${intent}&section_id=movitamins-cart-recommendations`
      )
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return [...doc.querySelectorAll('.mv-cart-rec')].map((el) => ({
            id: el.dataset.productId,
            html: el.outerHTML,
          }));
        })
        .catch(() => {
          cache.delete(key);
          return [];
        });
      cache.set(key, request);
    }
    return cache.get(key);
  };

  // Take items from each list in turn, so every product in the cart contributes.
  const merge = (lists, exclude) => {
    const seen = new Set(exclude);
    const result = [];
    const longest = Math.max(0, ...lists.map((list) => list.length));
    for (let i = 0; i < longest && result.length < MAX_ITEMS; i++) {
      for (const list of lists) {
        const item = list[i];
        if (item && !seen.has(item.id)) {
          seen.add(item.id);
          result.push(item);
          if (result.length >= MAX_ITEMS) break;
        }
      }
    }
    return result;
  };

  class MovitaminsCartRecs extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', this.onClick.bind(this));
    }

    connectedCallback() {
      this.track = this.querySelector('.mv-cart-recs__track');
      this.headingEl = this.querySelector('.mv-cart-recs__heading');
      this.ids = (this.dataset.productIds || '').split(',').filter(Boolean);
      this.key = this.ids.join(',');
      this.track.addEventListener(
        'scroll',
        () => {
          lastScroll = this.track.scrollLeft;
          this.updateArrows();
        },
        { passive: true }
      );

      if (lastRender) {
        const exclude = new Set(this.ids);
        const items = lastRender.items.filter((item) => !exclude.has(item.id));
        this.show(lastRender.heading, items, lastRender.key === this.key);
      }
      this.load();
    }

    async load() {
      const url = this.dataset.url;
      if (!url || !this.ids.length) return;
      const sources = this.ids.slice(0, MAX_SOURCE_PRODUCTS);

      let heading = this.dataset.heading;
      let items = merge(await Promise.all(sources.map((id) => fetchRecs(url, id, 'complementary'))), this.ids);

      // Too few complementary products for a slider: top up with related products.
      if (items.length < MIN_ITEMS && this.dataset.fallback === 'true') {
        const related = merge(
          await Promise.all(sources.slice(0, 2).map((id) => fetchRecs(url, id, 'related'))),
          [...this.ids, ...items.map((item) => item.id)]
        );
        if (!items.length) heading = this.dataset.fallbackHeading;
        items = [...items, ...related].slice(0, MAX_ITEMS);
      }

      if (!this.isConnected) return;
      const unchanged =
        lastRender &&
        lastRender.key === this.key &&
        lastRender.items.map((item) => item.id).join() === items.map((item) => item.id).join();
      lastRender = { key: this.key, heading, items };
      if (!unchanged || this.hidden) this.show(heading, items, lastRender.key === this.key);
    }

    show(heading, items, keepScroll) {
      if (!items.length) {
        this.hidden = true;
        return;
      }
      this.headingEl.textContent = heading;
      this.track.innerHTML = items.map((item) => item.html).join('');
      this.hidden = false;
      this.track.scrollLeft = keepScroll ? lastScroll : 0;
      this.updateArrows();
    }

    updateArrows() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const [prev, next] = this.querySelectorAll('.mv-cart-recs__arrow');
      if (!prev || !next) return;
      this.classList.toggle('mv-cart-recs--static', max <= 2);
      prev.disabled = this.track.scrollLeft <= 2;
      next.disabled = this.track.scrollLeft >= max - 2;
    }

    onClick(event) {
      const arrow = event.target.closest('.mv-cart-recs__arrow');
      if (arrow) {
        const card = this.track.querySelector('.mv-cart-rec');
        const step = card ? card.getBoundingClientRect().width + 12 : 160;
        this.track.scrollBy({ left: step * Number(arrow.dataset.direction), behavior: 'smooth' });
        return;
      }

      const opener = event.target.closest('[data-open-picker]');
      if (opener) {
        const picker = opener.parentElement.querySelector('.mv-cart-rec__picker');
        this.closePickers(picker);
        const open = picker.hidden;
        picker.hidden = !open;
        opener.setAttribute('aria-expanded', String(open));
        if (open) picker.querySelector('.mv-cart-rec__option:not([disabled])')?.focus();
        return;
      }

      if (event.target.closest('[data-close-picker]')) {
        this.closePickers();
        return;
      }

      const adder = event.target.closest('[data-variant-id]');
      if (adder && !adder.disabled) this.add(adder);
    }

    closePickers(except) {
      this.querySelectorAll('.mv-cart-rec__picker').forEach((picker) => {
        if (picker === except) return;
        picker.hidden = true;
        picker.parentElement.querySelector('[data-open-picker]')?.setAttribute('aria-expanded', 'false');
      });
    }

    async add(button) {
      const card = button.closest('.mv-cart-rec');
      const error = card.querySelector('.mv-cart-rec__error');
      if (card.classList.contains('is-loading')) return;
      card.classList.add('is-loading');
      error.hidden = true;

      const drawer = document.querySelector('cart-drawer');
      const sections = drawer ? drawer.getSectionsToRender().map((section) => section.id) : [];

      try {
        const response = await fetch(window.routes.cart_add_url, {
          ...fetchConfig('json'),
          body: JSON.stringify({
            items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (data.status) {
          error.textContent = data.description || data.message || window.cartStrings.error;
          error.hidden = false;
          return;
        }
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-recommendations', cartData: data });
        }
        if (drawer && data.sections) {
          drawer.renderContents({ id: data.items?.[0]?.product_id, sections: data.sections });
        } else {
          window.location.href = window.routes.cart_url;
        }
      } catch (e) {
        error.textContent = window.cartStrings.error;
        error.hidden = false;
      } finally {
        card.classList.remove('is-loading');
      }
    }
  }

  customElements.define('movitamins-cart-recs', MovitaminsCartRecs);
})();
